import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { message } from '@/utils/antdStatic';
import { WorkMode } from '../workMode';
import { getHomePath } from '../homeMenu';
import {
  MAX_TABS,
  activateTab,
  backTarget,
  closeOtherTabs,
  closeTab,
  keptPages,
  menuTarget,
  parseTabsState,
  pruneTabs,
  resolveTabKey,
  type TabsState,
} from './tabsModel';

const STORAGE_PREFIX = 'tenant_page_tabs_v1:';

/** 退出登录、切换租户时清掉标签记录 */
export const clearPageTabs = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(STORAGE_PREFIX))
    .forEach((key) => sessionStorage.removeItem(key));
};

/** 菜单树里的末级菜单：path → 显示名 */
const collectLeaves = (items: MenuProps['items'], out = new Map<string, ReactNode>()) => {
  for (const item of items ?? []) {
    if (!item || !('key' in item) || typeof item.key !== 'string') continue;
    if ('children' in item && item.children?.length) {
      collectLeaves(item.children, out);
    } else if (item.key.startsWith('/')) {
      out.set(item.key, 'label' in item ? item.label : item.key);
    }
  }
  return out;
};

export interface PageTabView {
  key: string;
  title: ReactNode;
  closable: boolean;
  /** 这个标签里要留着不卸载的页面 pathname */
  pages: string[];
}

/** 标签里一个页面的标识 */
export const pageId = (tabKey: string, pathname: string) => `${tabKey}|${pathname}`;

interface Options {
  workMode: WorkMode;
  menuItems: MenuProps['items'];
}

/** 标签记录存在会话存储里，只跟当前浏览器窗口走，管理模式和业务模式各一套 */
export const usePageTabs = ({ workMode, menuItems }: Options) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const homeKey = getHomePath(workMode);
  const storageKey = `${STORAGE_PREFIX}${workMode}`;

  const [store, setStore] = useState(() => ({
    key: storageKey,
    state: parseTabsState(sessionStorage.getItem(storageKey), homeKey),
  }));
  // 切换模式时换成对应那一套标签
  let state: TabsState = store.state;
  if (store.key !== storageKey) {
    state = parseTabsState(sessionStorage.getItem(storageKey), homeKey);
    setStore({ key: storageKey, state });
  }

  const [versions, setVersions] = useState<Record<string, number>>({});
  /** 本套标签已经成功归档过地址（值为 storageKey） */
  const settledRef = useRef<string | null>(null);
  /**
   * 正在关闭的当前标签。路由跳转是 transition，比状态更新晚到，
   * 等地址真的切到别的标签再删它，否则旧地址会把它重新打开。
   */
  const pendingCloseRef = useRef<string | null>(null);

  const menuIndex = useMemo(() => collectLeaves(menuItems), [menuItems]);
  const menuKeys = useMemo(() => new Set(menuIndex.keys()), [menuIndex]);

  const fullPath = location.pathname + location.search;
  const currentKey = resolveTabKey(state, location.pathname, fullPath, menuKeys, settledRef.current === storageKey);

  useEffect(() => {
    if (!currentKey) return;
    settledRef.current = storageKey;
    const closing = pendingCloseRef.current;
    if (closing === currentKey) return;
    pendingCloseRef.current = null;
    setStore((s) => {
      if (s.key !== storageKey) return s;
      const base = closing ? closeTab(s.state, closing, homeKey) : s.state;
      return { ...s, state: activateTab(base, currentKey, fullPath, navigationType === 'PUSH') };
    });
  }, [currentKey, fullPath, navigationType, storageKey, homeKey]);

  useEffect(() => {
    // 只有首页时说明菜单还没加载回来，不能拿它去裁剪
    if (menuKeys.size <= 1) return;
    setStore((s) => (s.key === storageKey ? { ...s, state: pruneTabs(s.state, menuKeys, homeKey) } : s));
  }, [menuKeys, storageKey, homeKey]);

  useEffect(() => {
    sessionStorage.setItem(store.key, JSON.stringify(store.state));
  }, [store]);

  const openMenu = useCallback((key: string) => {
    const target = menuTarget(state, key);
    if (target === null) {
      message.warning(`最多同时打开 ${MAX_TABS} 个标签页，请先关闭部分标签`);
      return;
    }
    // 标签之间的切换不留浏览记录
    navigate(target, { replace: key !== currentKey });
  }, [state, currentKey, navigate]);

  const selectTab = useCallback((key: string) => {
    const tab = state.tabs.find((t) => t.key === key);
    if (tab && key !== currentKey) navigate(tab.path, { replace: true });
  }, [state, currentKey, navigate]);

  const removeTab = useCallback((key: string) => {
    const next = closeTab(state, key, homeKey);
    if (key !== currentKey) {
      setStore({ key: storageKey, state: next });
      return;
    }
    const target = next.tabs.find((t) => t.key === next.activeKey);
    if (!target) return;
    pendingCloseRef.current = key;
    navigate(target.path, { replace: true });
  }, [state, homeKey, storageKey, currentKey, navigate]);

  /** 关闭全部：只留首页并切回首页 */
  const removeAllTabs = useCallback(() => {
    const next = closeOtherTabs(state, currentKey, homeKey);
    setStore({ key: storageKey, state: next });
    if (currentKey === homeKey) return;
    pendingCloseRef.current = currentKey;
    const home = next.tabs.find((t) => t.key === homeKey);
    navigate(home?.path ?? homeKey, { replace: true });
  }, [state, currentKey, homeKey, storageKey, navigate]);

  // 保持引用不变，隐藏的页面不会因为标签状态变化跟着重渲染
  const stateRef = useRef(state);
  stateRef.current = state;
  const goBack = useCallback((key: string) => {
    const target = backTarget(stateRef.current, key);
    if (target) navigate(target, { replace: true });
  }, [navigate]);

  /** 刷新只重载标签里正在显示的那一页，来时路上留着的页面不动 */
  const refreshTab = useCallback((key: string) => {
    const id = pageId(key, location.pathname);
    setVersions((v) => ({ ...v, [id]: (v[id] ?? 0) + 1 }));
  }, [location.pathname]);

  const tabs: PageTabView[] = state.tabs.map((tab) => ({
    key: tab.key,
    title: menuIndex.get(tab.key) ?? tab.key,
    closable: tab.key !== homeKey,
    pages: keptPages(tab),
  }));

  return { tabs, currentKey, versions, openMenu, selectTab, removeTab, removeAllTabs, refreshTab, goBack };
};
